import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";
import { ClampedText } from "./ClampedText";
import { parseNoteText } from "./noteMentions";
import { personName } from "./ticketHelpers";

/** The part of an agent the chip needs to resolve a name. */
export interface MentionableAgent {
  givenName?: string | null;
  familyName?: string | null;
}

/**
 * A mention inside running text. Not interactive: no link, no hover card. The chip that
 * mentions the viewer reads in the accent colour, every other one in the ink colour.
 */
export function MentionChip({
  label,
  isViewer,
}: {
  label: string;
  isViewer: boolean;
}) {
  return (
    <span
      data-slot="mention-chip"
      className={cn(
        "rounded-sm bg-primary-subtle px-1 text-label",
        isViewer ? "text-primary-strong" : "text-ink",
      )}
    >
      @{label}
    </span>
  );
}

interface NoteBodyProps {
  /** The stored note text: plain text with `@[Label](user:<uuid>)` tokens. */
  text: string;
  viewerId?: string;
  agentsById: ReadonlyMap<string, MentionableAgent>;
  /** Clamp to 12 lines with a "Mehr anzeigen" toggle. False for a deep-linked note (D-15). */
  clamp?: boolean;
}

/**
 * A note's body. The stored text is split into React text nodes and chip spans; nothing is
 * ever injected as HTML and nothing is auto-linked (a note is written by any colleague of
 * the tenant, T-02-22). A mention shows the colleague's current name, else the label the
 * token was written with, so a removed colleague stays readable.
 */
export function NoteBody({
  text,
  viewerId,
  agentsById,
  clamp = true,
}: NoteBodyProps) {
  const { t } = useTranslation();
  // Chips are keyed by colleague and occurrence: the same person can be mentioned twice.
  const occurrences = new Map<string, number>();
  const nodes = parseNoteText(text).map((segment) => {
    if (segment.kind === "text") return segment.text;
    const n = (occurrences.get(segment.userId) ?? 0) + 1;
    occurrences.set(segment.userId, n);
    return (
      <MentionChip
        key={`${segment.userId}:${n}`}
        label={
          personName(agentsById.get(segment.userId)) ??
          (segment.label || t("tickets.notes.unknownAuthor"))
        }
        isViewer={segment.userId === viewerId?.toLowerCase()}
      />
    );
  });

  return (
    <ClampedText textKey={text} clamp={clamp}>
      {nodes}
    </ClampedText>
  );
}
